import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { usePlaidLink } from "react-plaid-link";
import { toast } from "sonner";
import { Zap, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PlaidLinkButton
 * ---------------
 * Fetches a Plaid link_token from the backend, opens the Plaid Link widget on
 * demand, then exchanges the public_token + fires an initial sync via the
 * backend. Emits `onLinked({ item_id, institution_name })` on success so the
 * parent can close its dialog and refresh the dashboard.
 *
 * Usage:
 *   <PlaidLinkButton onLinked={(res) => { closeDialog(); refresh(); }} />
 */
const PlaidLinkButton = ({
  onLinked,
  onExit,
  className = "",
  label = "Connect a bank via Plaid",
  autoOpen = false,
  disabled = false,
  testid = "plaid-link-button",
}) => {
  const [linkToken, setLinkToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exchanging, setExchanging] = useState(false);

  const fetchLinkToken = useCallback(async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/plaid/link_token`, {}, { withCredentials: true });
      setLinkToken(res.data?.link_token || null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't start Plaid Link");
      setLinkToken(null);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!linkToken) fetchLinkToken();
  }, [linkToken, fetchLinkToken]);

  const onSuccess = useCallback(
    async (publicToken, metadata) => {
      setExchanging(true);
      try {
        const res = await axios.post(
          `${API}/plaid/exchange`,
          {
            public_token: publicToken,
            institution_id: metadata?.institution?.institution_id || null,
            institution_name: metadata?.institution?.name || null,
          },
          { withCredentials: true }
        );
        toast.success(`Linked ${metadata?.institution?.name || "your bank"}`);
        if (onLinked) onLinked(res.data);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Plaid exchange failed");
      } finally {
        setExchanging(false);
      }
    },
    [onLinked]
  );

  const onLinkExit = useCallback(
    (err) => {
      if (err && err.error_code) {
        toast.error(err.display_message || err.error_message || err.error_code);
      }
      if (onExit) onExit(err);
    },
    [onExit]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit: onLinkExit,
  });

  useEffect(() => {
    if (autoOpen && ready && linkToken) open();
  }, [autoOpen, ready, linkToken, open]);

  const isBusy = busy || exchanging || !ready;

  return (
    <button
      type="button"
      onClick={() => open()}
      disabled={isBusy || disabled || !linkToken}
      data-testid={testid}
      className={
        className ||
        "inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-5 py-2.5 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_10px_30px_-12px_rgba(0,230,118,0.6)]"
      }
    >
      {isBusy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
      {exchanging ? "Syncing your bank…" : busy ? "Preparing Plaid…" : label}
    </button>
  );
};

export default PlaidLinkButton;
